/**
 * Web version of a shared receipt split, for friends without the Pebla app:
 * they enter their details, the receipt prints out, they tick what they had
 * (several people can share an item and split its cost), see what they owe
 * and pay with MTN MoMo.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { downloadLinks } from '../constants/links';
import {
  Invite,
  ReceiptItem,
  Share,
  addedTax,
  canPickItems,
  Payer,
  chargesOf,
  choices,
  firstName,
  fixedShare,
  formatMomoNumber,
  formatRwf,
  grandTotal,
  initials,
  isExpired,
  isValidPayerName,
  itemTotal,
  liveShare,
  loadPayer,
  memberIdFor,
  momoDialString,
  normalizeMomoNumber,
  payAmount,
  readInvite,
  round2,
  savePayer,
  shareTotal,
  sharerName,
  subtotal,
  telHref,
  unpickedTotal,
} from './model';
import { LiveStatus, Member, useSplitRoom } from './live';
import './splitReceipt.css';

type Load = { state: 'loading' } | { state: 'invalid' } | { state: 'ready'; invite: Invite };

const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

export const SplitReceiptPage: React.FC = () => {
  const { search } = useLocation();
  const [load, setLoad] = useState<Load>({ state: 'loading' });

  useEffect(() => {
    let alive = true;
    readInvite(search).then((invite) => {
      if (alive) setLoad(invite ? { state: 'ready', invite } : { state: 'invalid' });
    });
    return () => {
      alive = false;
    };
  }, [search]);

  useEffect(() => {
    document.title = 'Split the bill · Pebla';
  }, []);

  return (
    <div className="sr-page">
      <TopBar search={search} />
      {load.state === 'loading' && <p className="sr-muted sr-center">Opening the bill…</p>}
      {load.state === 'invalid' && (
        <Notice
          title="This link doesn’t work"
          message="It may be incomplete. Ask the person who shared it to send it again."
        />
      )}
      {load.state === 'ready' &&
        (isExpired(load.invite.split) ? (
          <Notice
            title="This split has expired"
            message="Ask the person who shared it for a new link."
          />
        ) : (
          <Flow invite={load.invite} />
        ))}
    </div>
  );
};

// Split links are web-only for now; the in-app version is switched off.
const TopBar: React.FC<{ search: string }> = () => (
  <header className="sr-topbar">
    <div className="sr-brand">
      <img src="/images/icons/pe.svg" alt="" width={28} height={28} />
      <span>Pebla</span>
    </div>
    {/* {isMobile() && (
      <a className="sr-open-app" href={`pebla://split-receipt${search}`}>
        Open in app
      </a>
    )} */}
  </header>
);

/** Asks who is paying, then shows the receipt to pick from. */
const Flow: React.FC<{ invite: Invite }> = ({ invite }) => {
  const [payer, setPayer] = useState<Payer | null>(null);
  const [saved, setSaved] = useState(() => loadPayer(invite.split.id));

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [payer]);

  if (payer === null) {
    return (
      <DetailsStep
        invite={invite}
        initial={saved}
        onContinue={(p) => {
          savePayer(invite.split.id, p);
          setSaved(p);
          setPayer(p);
        }}
      />
    );
  }
  return <Invitation invite={invite} payer={payer} onChangeDetails={() => setPayer(null)} />;
};

const DetailsStep: React.FC<{
  invite: Invite;
  initial: Payer | null;
  onContinue: (payer: Payer) => void;
}> = ({ invite, initial, onContinue }) => {
  const { split } = invite;
  const [name, setName] = useState(initial?.name ?? '');
  const [momo, setMomo] = useState(initial ? formatMomoNumber(initial.momo) : '');
  const [agreed, setAgreed] = useState(false);
  const [touched, setTouched] = useState({ name: false, momo: false });
  const [tried, setTried] = useState(false);

  const merchant = split.destination.type === 'merchant';
  const vendor = split.receipt.merchantName.trim();
  const number = normalizeMomoNumber(momo);
  const nameOk = isValidPayerName(name);
  const nameError = touched.name && !nameOk ? 'Enter your name' : null;
  const momoError =
    touched.momo && number === null
      ? momo.trim() === ''
        ? 'Enter your MTN MoMo number'
        : 'Enter an MTN MoMo number, e.g. 0788 123 456'
      : null;
  const ready = nameOk && number !== null && agreed;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, momo: true });
    setTried(true);
    if (!ready || number === null) return;
    onContinue({ name: name.trim().replace(/\s+/g, ' '), momo: number });
  };

  return (
    <form onSubmit={submit} noValidate>
      <main className="sr-main">
        <section className="sr-host">
          <div className="sr-host-icon" aria-hidden>
            🧾
          </div>
          <div>
            <h1>{merchant ? 'Split the bill' : `${sharerName(split)} shared a bill`}</h1>
            <p>
              {[vendor, `Total ${formatRwf(grandTotal(split.receipt))}`].filter(Boolean).join(' · ')}
            </p>
          </div>
        </section>

        <section>
          <h2 className="sr-label">YOUR DETAILS</h2>
          <div className="sr-card sr-form">
            <label className="sr-field">
              <span>Full name</span>
              <input
                type="text"
                autoComplete="name"
                autoCapitalize="words"
                placeholder="e.g. Aline Uwase"
                value={name}
                aria-invalid={nameError !== null}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              />
              {nameError && <small className="sr-field-error">{nameError}</small>}
            </label>
            <label className="sr-field">
              <span>MTN MoMo number</span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="0788 123 456"
                value={momo}
                aria-invalid={momoError !== null}
                onChange={(e) => setMomo(e.target.value)}
                onBlur={() => {
                  setTouched((t) => ({ ...t, momo: true }));
                  if (number) setMomo(formatMomoNumber(number));
                }}
              />
              {momoError ? (
                <small className="sr-field-error">{momoError}</small>
              ) : (
                <small className="sr-muted">The number you’ll pay your share from.</small>
              )}
            </label>
          </div>
        </section>

        <label
          className={`sr-confirm ${agreed ? 'is-on' : ''} ${tried && !agreed ? 'needs-attention' : ''}`}
        >
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span className={`sr-check ${agreed ? 'is-on' : ''}`} aria-hidden>
            {agreed ? '✓' : ''}
          </span>
          <span>
            I confirm my name and MTN MoMo number are correct, and that I’ll pay my share from this
            number.
          </span>
        </label>
      </main>

      <footer className="sr-bottom">
        <button type="submit" className={`sr-pay ${ready ? '' : 'is-disabled'}`} aria-disabled={!ready}>
          {canPickItems(split) ? 'Continue to items' : 'Continue to my share'}
        </button>
      </footer>
    </form>
  );
};

const Notice: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div className="sr-notice">
    <div className="sr-notice-icon" aria-hidden>
      ⏱
    </div>
    <h1>{title}</h1>
    <p className="sr-muted">{message}</p>
    <GetTheApp />
  </div>
);

const Invitation: React.FC<{ invite: Invite; payer: Payer; onChangeDetails: () => void }> = ({
  invite,
  payer,
  onChangeDetails,
}) => {
  const { split } = invite;
  const people = choices(split);
  const live = canPickItems(split);
  // Only splits with a share set per person ask who you are; on pick-items
  // splits everyone just ticks what they had.
  const asksWhoYouAre = !live && people.length > 1 && !invite.participantId;
  const hi = firstName(payer.name);
  const nameMatch = useMemo(() => {
    const match = people.filter((p) => firstName(p.name).toLowerCase() === hi.toLowerCase());
    return match.length === 1 ? match[0].id : null;
  }, [people, hi]);

  // Someone the app already put on the split keeps their id (and picks).
  const memberId = useMemo(
    () => invite.participantId ?? nameMatch ?? memberIdFor(split.id),
    [invite.participantId, nameMatch, split.id],
  );
  const room = useSplitRoom(
    split,
    live ? { id: memberId, name: payer.name, momo: payer.momo } : null,
  );
  const { picks, members, closed } = live
    ? room.state
    : { picks: split.assignments, members: room.state.members, closed: false };

  const [chosen, setChosen] = useState(() =>
    invite.participantId ?? (people.length === 1 ? people[0].id : nameMatch ?? ''),
  );
  const me = live ? memberId : chosen;
  const [stage, setStage] = useState<'pick' | 'paying' | 'paid'>('pick');

  const toggle = (item: ReceiptItem) => {
    if (live && !closed) room.toggle(item.id);
  };

  const share = live ? liveShare(split.receipt, picks, me) : fixedShare(split, me);
  const total = round2(shareTotal(share));
  const pay = payAmount(total);
  const count = live ? split.receipt.items.filter((i) => (picks[i.id] ?? []).includes(me)).length : 0;
  const unpicked = live ? round2(unpickedTotal(split.receipt, picks)) : 0;
  const merchant = split.destination.type === 'merchant';
  const sharer = sharerName(split);
  const dial = momoDialString(split.destination, pay);
  const problem =
    me === '' ? 'Choose who you are first' : total <= 0 ? 'Pick at least one item' : null;

  if (stage === 'paid') {
    return (
      <div className="sr-notice">
        <div className="sr-notice-icon sr-ok" aria-hidden>
          ✓
        </div>
        <h1>Thanks, {hi}</h1>
        <p className="sr-big">{formatRwf(pay)}</p>
        <p className="sr-muted">
          {merchant
            ? `Your share went to ${split.destination.name || 'the vendor'}.`
            : `Your share went to ${split.destination.name || sharer}. They’ll see it in their MoMo messages.`}
        </p>
        <GetTheApp />
      </div>
    );
  }

  return (
    <>
      <main className="sr-main">
        <section className="sr-host">
          <div className="sr-host-icon" aria-hidden>
            🧾
          </div>
          <div>
            <h1>{merchant ? 'Split the bill' : `${sharer} shared a bill`}</h1>
            <p>
              {me === ''
                ? `Hi ${hi}, choose your name to see your share.`
                : !live
                ? `Hi ${hi}, your share is set. Check it, then pay.`
                : closed
                ? `Hi ${hi}, picking has closed. Your share is final.`
                : `Hi ${hi}, tick what you had. Shared something? Tick it too and the cost is split.`}
            </p>
          </div>
        </section>

        <div className="sr-card sr-payer">
          <span className="sr-payer-avatar" aria-hidden>
            {hi[0]?.toUpperCase()}
          </span>
          <div>
            <strong>{payer.name}</strong>
            <span className="sr-muted sr-small">Paying from {formatMomoNumber(payer.momo)}</span>
          </div>
          <button type="button" className="sr-link" onClick={onChangeDetails}>
            Change
          </button>
        </div>

        {asksWhoYouAre && (
          <section>
            <h2 className="sr-label">WHO ARE YOU?</h2>
            <div className="sr-chips">
              {people.map((p) => (
                <button
                  key={p.id}
                  className={`sr-chip ${me === p.id ? 'is-on' : ''}`}
                  onClick={() => setChosen(p.id)}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </section>
        )}

        {live && <People members={members} picks={picks} me={me} status={room.status} />}

        <Printer>
          <Paper
            invite={invite}
            me={me}
            picks={picks}
            members={members}
            pickable={live && !closed}
            onToggle={toggle}
            unpicked={live ? { amount: unpicked, coveredBy: sharer } : null}
            summary={me === '' ? null : <PickSummary share={share} count={live ? count : null} />}
          />
        </Printer>

        <section>
          <h2 className="sr-label">MONEY GOES TO</h2>
          <div className="sr-card sr-dest">
            <span className="sr-dest-icon" aria-hidden>
              {merchant ? '🏪' : '👤'}
            </span>
            <div>
              <strong>{split.destination.name || (merchant ? 'Vendor' : sharer)}</strong>
              <span className="sr-muted">
                {merchant
                  ? `Vendor code ${split.destination.identifier}`
                  : `MTN MoMo · ${split.destination.identifier}`}
              </span>
            </div>
          </div>
        </section>

        <GetTheApp />
      </main>

      <footer className="sr-bottom">
        {live && (
          <div className="sr-bottom-row" aria-live="polite">
            <span className="sr-muted">{count === 1 ? '1 item picked' : `${count} items picked`}</span>
            <strong>{formatRwf(total)}</strong>
          </div>
        )}
        {stage === 'pick' && problem === null && pay !== total && (
          <p className="sr-muted sr-small sr-center sr-round-note">
            MoMo takes whole francs, so you’ll pay {formatRwf(pay)}.
          </p>
        )}
        {stage === 'pick' && problem !== null && (
          <button className="sr-pay is-disabled" disabled>
            {problem}
          </button>
        )}
        {stage === 'pick' && problem === null && (
          <a
            className="sr-pay"
            href={telHref(dial)}
            onClick={(e) => {
              setStage('paying');
              if (!isMobile()) e.preventDefault();
            }}
          >
            {`Pay ${formatRwf(pay)} with MoMo`}
          </a>
        )}
        {stage !== 'pick' && (
          <PayingHelp
            dial={dial}
            onPaid={() => {
              if (live) room.markPaid(pay);
              setStage('paid');
            }}
            onBack={() => setStage('pick')}
          />
        )}
      </footer>
    </>
  );
};

const PayingHelp: React.FC<{ dial: string; onPaid: () => void; onBack: () => void }> = ({
  dial,
  onPaid,
  onBack,
}) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(dial);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked; the code is still on screen */
    }
  };
  return (
    <div className="sr-paying">
      <p className="sr-muted sr-small sr-center">
        {isMobile()
          ? `Your phone should open the dialer${isIos() ? '. Tap call to pay' : ''}. If it doesn’t, copy the payment code and paste it in your phone app.`
          : 'Copy the payment code and dial it on your phone.'}
      </p>
      <div className="sr-paying-actions">
        <button className="sr-ghost" onClick={copy} aria-live="polite">
          {copied ? 'Copied ✓' : 'Copy code'}
        </button>
        <button className="sr-pay" onClick={onPaid}>
          I’ve paid
        </button>
      </div>
      <button className="sr-back" onClick={onBack}>
        Change my items
      </button>
    </div>
  );
};

/** Feeds the paper out of a little printer once, then leaves it be. */
const Printer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [done, setDone] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );
  return (
    <section className={`sr-printer-wrap ${done ? 'is-done' : ''}`}>
      <div className="sr-printer" aria-hidden>
        <span className="sr-printer-name">PEBLA</span>
        <span className="sr-printer-led" />
        <span className="sr-printer-slot" />
      </div>
      <div className="sr-feed">
        <div className="sr-feed-inner" onAnimationEnd={() => setDone(true)}>
          {children}
        </div>
      </div>
      {!done && <p className="sr-muted sr-center sr-small">Printing your receipt…</p>}
    </section>
  );
};

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const Paper: React.FC<{
  invite: Invite;
  me: string;
  picks: Record<string, string[]>;
  members: Member[];
  pickable: boolean;
  onToggle: (item: ReceiptItem) => void;
  unpicked: { amount: number; coveredBy: string } | null;
  summary: React.ReactNode;
}> = ({ invite, me, picks, members, pickable, onToggle, unpicked, summary }) => {
  const { receipt } = invite.split;
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const name = receipt.merchantName.trim();
  const date = receipt.date ? new Date(`${receipt.date}T00:00:00`) : null;
  const details = [
    receipt.location.trim(),
    date && !isNaN(date.getTime()) ? dateFormat.format(date) : '',
  ].filter(Boolean);
  const count = receipt.items.reduce((sum, i) => sum + i.quantity, 0);
  const c = receipt.charges;

  return (
    <article className="sr-paper">
      <div className="sr-monogram" aria-hidden>
        {name ? name[0].toUpperCase() : '🧾'}
      </div>
      <h2 className="sr-vendor">{name || 'Receipt'}</h2>
      {details.length > 0 && <p className="sr-muted sr-center sr-small">{details.join('  ·  ')}</p>}
      <hr className="sr-dash" />
      <div className="sr-caps-row">
        <span>ITEM</span>
        <span>{count === 1 ? '1 ITEM' : `${count} ITEMS`}</span>
      </div>
      <ul className="sr-items">
        {receipt.items.map((item, i) => (
          <ItemRow
            key={item.id}
            index={i}
            item={item}
            me={me}
            owners={(picks[item.id] ?? []).map(
              (id) => byId.get(id) ?? { id, name: '?', host: false, paid: false },
            )}
            paid={invite.paidItemIds.has(item.id)}
            pickable={pickable}
            onToggle={onToggle}
          />
        ))}
      </ul>
      <hr className="sr-dash" />
      <div className="sr-totals">
        <Row label="Subtotal" amount={subtotal(receipt)} />
        {c.tax !== 0 && (
          <Row
            label={c.taxIncluded ? 'VAT' : 'Tax'}
            note={c.taxIncluded ? 'Included in prices' : undefined}
            amount={c.taxIncluded ? c.tax : addedTax(c)}
          />
        )}
        {c.serviceCharge !== 0 && <Row label="Service charge" amount={c.serviceCharge} />}
        {c.gratuity !== 0 && <Row label="Gratuity" amount={c.gratuity} />}
        {c.otherCharges !== 0 && (
          <Row label={c.otherCharges < 0 ? 'Discount' : 'Other charges'} amount={c.otherCharges} />
        )}
        <div className="sr-grand">
          <span>TOTAL</span>
          <strong>{formatRwf(grandTotal(receipt))}</strong>
        </div>
        {unpicked && unpicked.amount > 0 && (
          <Row label="Not picked yet" note={`${unpicked.coveredBy} covers this`} amount={unpicked.amount} />
        )}
      </div>
      {summary && (
        <>
          <hr className="sr-dash" />
          {summary}
        </>
      )}
      <p className="sr-footer">SPLIT WITH PEBLA</p>
    </article>
  );
};

const joinNames = (names: string[]) =>
  names.length <= 2 ? names.join(' & ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;

const ItemRow: React.FC<{
  index: number;
  item: ReceiptItem;
  me: string;
  owners: Member[];
  paid: boolean;
  pickable: boolean;
  onToggle: (item: ReceiptItem) => void;
}> = ({ index, item, me, owners, paid, pickable, onToggle }) => {
  const checked = owners.some((m) => m.id === me);
  const others = owners.filter((m) => m.id !== me).map((m) => firstName(m.name));
  const ways = owners.length;
  let caption: string | null = null;
  if (checked && ways > 1) caption = `Split ${ways} ways · ${formatRwf(itemTotal(item) / ways)} each`;
  else if (!checked && others.length > 0)
    caption = `${joinNames(others)} ${others.length === 1 ? 'has' : 'have'} this${pickable ? ' · tap to share' : ''}`;
  else if (paid) caption = 'Paid in the app';
  const itemName = item.name.trim() || 'Unnamed item';

  const content = (
    <>
      <span className="sr-qty">{item.quantity}×</span>
      <span className="sr-item-text">
        <span className="sr-item-name">{itemName}</span>
        {item.quantity > 1 && <span className="sr-small sr-muted">{formatRwf(item.unitPrice)} each</span>}
        {owners.length > 0 && <Avatars members={owners} me={me} />}
        {caption && <span className="sr-small sr-caption">{caption}</span>}
      </span>
      <span className="sr-price">{formatRwf(itemTotal(item))}</span>
      {pickable && (
        <span className={`sr-check ${checked ? 'is-on' : ''}`} aria-hidden>
          {checked ? '✓' : ''}
        </span>
      )}
    </>
  );

  return (
    <li
      className={`sr-item ${checked && pickable ? 'is-on' : ''}`}
      style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}
    >
      {pickable ? (
        <button
          role="checkbox"
          aria-checked={checked}
          aria-label={`${itemName}, ${formatRwf(itemTotal(item))}${caption ? `. ${caption}` : ''}`}
          onClick={() => onToggle(item)}
        >
          {content}
        </button>
      ) : (
        <div>{content}</div>
      )}
    </li>
  );
};

/** Initials of everyone on an item, newest last; you're highlighted. */
const Avatars: React.FC<{ members: Member[]; me: string }> = ({ members, me }) => {
  const shown = members.slice(0, 4);
  const extra = members.length - shown.length;
  return (
    <span className="sr-avatars" aria-hidden>
      {shown.map((m) => (
        <span
          key={m.id}
          className={`sr-avatar ${m.id === me ? 'is-me' : ''} ${m.host ? 'is-host' : ''}`}
          title={m.name}
        >
          {initials(m.name) || '?'}
        </span>
      ))}
      {extra > 0 && <span className="sr-avatar is-more">+{extra}</span>}
    </span>
  );
};

/** Everyone on the bill and whether they've paid, with the live connection state. */
const People: React.FC<{
  members: Member[];
  picks: Record<string, string[]>;
  me: string;
  status: LiveStatus;
}> = ({ members, picks, me, status }) => {
  const picking = new Set(Object.values(picks).flat());
  const shown = members.filter((m) => m.id === me || m.host || picking.has(m.id) || m.paid);
  const statusText =
    status === 'live' ? 'Live' : status === 'connecting' ? 'Connecting…' : status === 'offline' ? 'Reconnecting…' : null;
  return (
    <section>
      <div className="sr-label-row">
        <h2 className="sr-label">ON THIS BILL</h2>
        {statusText && (
          <span className={`sr-live is-${status}`} aria-live="polite">
            <span className="sr-live-dot" aria-hidden />
            {statusText}
          </span>
        )}
      </div>
      <div className="sr-people">
        {shown.map((m) => (
          <span key={m.id} className={`sr-person ${m.id === me ? 'is-me' : ''}`}>
            <span className={`sr-avatar ${m.id === me ? 'is-me' : ''} ${m.host ? 'is-host' : ''}`} aria-hidden>
              {initials(m.name) || '?'}
            </span>
            <span>
              {m.id === me ? 'You' : firstName(m.name)}
              {m.host && <span className="sr-muted"> · host</span>}
              {m.paid && <span className="sr-paid-tag"> · paid ✓</span>}
            </span>
          </span>
        ))}
      </div>
    </section>
  );
};

const Row: React.FC<{ label: string; amount: number; note?: string }> = ({ label, amount, note }) => (
  <div className="sr-row">
    <span className="sr-muted">{label}</span>
    {note && <span className="sr-small sr-muted">{note}</span>}
    <span className="sr-row-amount">{formatRwf(amount)}</span>
  </div>
);

const PickSummary: React.FC<{ share: Share; count: number | null }> = ({ share, count }) => (
  <div className="sr-summary" aria-live="polite">
    <div className="sr-caps-row">
      <span>YOUR PICK</span>
      {count !== null && <span>{count === 1 ? '1 ITEM' : `${count} ITEMS`}</span>}
    </div>
    {count === 0 ? (
      <p className="sr-muted sr-small">Tick the items you had above.</p>
    ) : (
      <>
        <Row label="Your items" amount={share.itemsAmount} />
        {chargesOf(share) !== 0 && <Row label="Your part of tax and charges" amount={chargesOf(share)} />}
      </>
    )}
    <div className="sr-grand sr-you-pay">
      <span>YOU PAY</span>
      <strong>{formatRwf(shareTotal(share))}</strong>
    </div>
  </div>
);

const GetTheApp: React.FC = () => (
  <section className="sr-card sr-get-app">
    <strong>Split bills faster with Pebla</strong>
    <span className="sr-muted sr-small">
      Scan a receipt, share the link, and see who has paid.
    </span>
    <div className="sr-stores">
      <a href={downloadLinks.playStore} target="_blank" rel="noopener noreferrer">
        <img src="/images/download/play-store.svg" alt="Get it on Google Play" />
      </a>
      <a href={downloadLinks.appStore} target="_blank" rel="noopener noreferrer">
        <img src="/images/download/app-store.svg" alt="Download on the App Store" />
      </a>
    </div>
  </section>
);
